import { SEO } from "@/components/SEO";
import { PageHero } from "@/components/PageHero";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Building2, ClipboardCheck, Map, FileSearch, Users, ShieldCheck, Banknote, Sparkles, HeartHandshake, Gauge } from "lucide-react";

const cosaFacciamo = [
  { icon: FileSearch, title: "Diagnosi del territorio", text: "Sopralluoghi tecnici su edifici pubblici, scuole, piazze, percorsi pedonali e fermate del trasporto." },
  { icon: ClipboardCheck, title: "MiniPEBA", text: "Piano di Eliminazione delle Barriere Architettoniche in versione agile: priorità, costi stimati e cronoprogramma." },
  { icon: Map, title: "Mappatura accessibilità", text: "Censimento georeferenziato di criticità e buone pratiche per pianificare investimenti mirati." },
  { icon: Users, title: "Formazione e affiancamento", text: "Tavoli con UTC, polizia locale e servizi sociali per diffondere cultura dell'Universal Design." },
];

const miniPebaSteps = [
  "Riunione di kick-off con l'Amministrazione e gli uffici tecnici",
  "Sopralluoghi mirati su edifici e percorsi prioritari",
  "Schede di rilievo fotografiche conformi al DM 236/89 e DPR 503/96",
  "Stima parametrica degli interventi e individuazione delle priorità",
  "Documento finale consegnato in formato PDF accessibile + dataset GIS",
  "Presentazione pubblica e supporto alla candidatura su bandi PNRR/regionali",
];

const perche = [
  { icon: Sparkles, title: "25 anni di esperienza", text: "Dal 2000 affianchiamo Enti pubblici su tutto il territorio nazionale: una memoria tecnica che si traduce in scelte rapide e affidabili." },
  { icon: ShieldCheck, title: "Conformità normativa garantita", text: "Output sempre allineati a DM 236/89, DPR 503/96, Linee Guida MIT e standard UNI/CEN sull'Universal Design." },
  { icon: Banknote, title: "Strumento finanziabile", text: "Il MiniPEBA è progettato per essere immediatamente spendibile su bandi PNRR, fondi regionali e Politica di Coesione." },
  { icon: Gauge, title: "Tempi certi, 60–90 giorni", text: "Metodo collaudato che restituisce all'Amministrazione un piano operativo in pochi mesi, non in anni." },
  { icon: HeartHandshake, title: "Ente del Terzo Settore indipendente", text: "Nessun conflitto di interesse con imprese di costruzioni o fornitori: lavoriamo solo nell'interesse dei cittadini." },
  { icon: Users, title: "Approccio multidisciplinare", text: "Architetti, ingegneri, esperti di mobilità, pedagogisti e persone con disabilità nello stesso tavolo di lavoro." },
];


const ServiziComuni = () => (
  <>
    <SEO
      title="Servizi per i Comuni — MiniPEBA | FIABA ETS"
      description="FIABA affianca Comuni e Enti pubblici nel superamento delle barriere architettoniche con il MiniPEBA: diagnosi rapida, priorità d'intervento e supporto alla progettazione."
      path="/servizi-comuni"
    />
    <PageHero
      eyebrow="Servizi per la Pubblica Amministrazione"
      title="Affianchiamo i Comuni verso città accessibili"
      subtitle="Strumenti agili, dati concreti, esperienza venticinquennale al servizio degli Enti locali."
    />

    <section className="section-padding">
      <div className="container-narrow space-y-12">
        <div className="max-w-3xl">
          <h2 className="font-serif text-3xl font-bold text-primary mb-4">Cosa fa FIABA per la PA</h2>
          <p className="text-foreground/80 leading-relaxed">
            Da oltre 25 anni lavoriamo a fianco di Comuni, Province e Regioni per trasformare gli obblighi
            normativi in opportunità reali di inclusione. Offriamo competenze multidisciplinari — architetti,
            ingegneri, esperti di mobilità e di inclusione sociale — per costruire piani concreti, finanziabili
            e misurabili.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {cosaFacciamo.map((s) => (
            <div key={s.title} className="bg-card border border-border rounded-lg p-6 shadow-soft hover:shadow-elegant transition-shadow">
              <s.icon className="w-10 h-10 text-secondary mb-4" aria-hidden="true" />
              <h3 className="font-semibold text-lg text-primary mb-2">{s.title}</h3>
              <p className="text-sm text-foreground/80 leading-relaxed">{s.text}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="section-padding bg-muted/40">
      <div className="container-narrow grid lg:grid-cols-5 gap-10">
        <div className="lg:col-span-2 space-y-4">
          <span className="inline-block bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full">Servizio di punta</span>
          <h2 className="font-serif text-3xl font-bold text-primary">Il MiniPEBA</h2>
          <p className="text-foreground/80 leading-relaxed">
            Un PEBA "leggero", consegnato in <strong>60–90 giorni</strong>, pensato per i Comuni che vogliono
            partire subito senza attendere mesi di progettazione. Strumento operativo, immediatamente utile
            per partecipare a bandi PNRR e finanziamenti regionali sull'accessibilità.
          </p>
          <p className="text-foreground/80 leading-relaxed">
            Il MiniPEBA è la base ideale per costruire successivamente un PEBA esteso o un PAU
            (Piano per l'Accessibilità Urbana).
          </p>
        </div>
        <div className="lg:col-span-3 bg-card border border-border rounded-lg p-7 shadow-elegant">
          <h3 className="font-semibold text-primary mb-5 flex items-center gap-2"><Building2 className="w-5 h-5 text-secondary" /> Come funziona</h3>
          <ol className="space-y-3">
            {miniPebaSteps.map((step, i) => (
              <li key={i} className="flex gap-3 text-sm text-foreground/85">
                <span className="shrink-0 w-7 h-7 rounded-full bg-primary text-primary-foreground font-semibold text-xs flex items-center justify-center">{i + 1}</span>
                <span className="pt-1">{step}</span>
              </li>
            ))}
          </ol>
        </div>
      </div>
    </section>

    <section className="section-padding">
      <div className="container-narrow">
        <span className="inline-block bg-secondary text-secondary-foreground text-xs font-semibold uppercase tracking-wider px-3 py-1 rounded-full mb-4">Perché sceglierci</span>
        <h2 className="font-serif text-3xl font-bold text-primary mb-3">Sei buoni motivi per affidarti a FIABA</h2>
        <p className="text-foreground/70 mb-10 max-w-2xl">La differenza tra un piano che resta nel cassetto e uno che diventa cantiere.</p>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {perche.map((p) => (
            <div key={p.title} className="bg-card border border-border rounded-lg p-6 shadow-soft hover:shadow-elegant transition-shadow">
              <p.icon className="w-9 h-9 text-secondary mb-3" aria-hidden="true" />
              <h3 className="font-semibold text-primary mb-2">{p.title}</h3>
              <p className="text-sm text-foreground/80 leading-relaxed">{p.text}</p>
            </div>
          ))}
        </div>
        <div className="mt-12 bg-hero text-primary-foreground p-8 rounded-lg shadow-elegant flex flex-col md:flex-row items-start md:items-center justify-between gap-5">
          <div>
            <h3 className="font-serif text-2xl font-bold mb-2">Vuoi attivare il MiniPEBA nel tuo Comune?</h3>
            <p className="text-primary-foreground/90 text-sm">Scrivici per ricevere una proposta tecnico-economica personalizzata.</p>
          </div>
          <Button asChild size="lg" className="bg-gold text-secondary-foreground hover:opacity-90 font-semibold">
            <Link to="/contatti">Richiedi un incontro</Link>
          </Button>
        </div>
      </div>
    </section>
  </>
);

export default ServiziComuni;
